import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import type { Food } from "@corpuscomplete/core/nutrition/food";
import type { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { FoodItemLabel } from "@corpuscomplete/web/components/nutrition/food-item";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@corpuscomplete/web-ui/components/popover";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { ChevronRightIcon } from "lucide-react";

export type FoodSearchDialogResultsHandle = {
  handleKeyDown: (
    event: Pick<
      KeyboardEvent<HTMLInputElement> | globalThis.KeyboardEvent,
      "key" | "target" | "preventDefault" | "stopPropagation"
    >,
  ) => void;
};

type FlatRow = {
  sectionTitle: string;
  sectionExpandButton: FoodSearchUI.Section["expandButton"];
  row: FoodSearchUI.Row;
};

export const FoodSearchDialogResults = forwardRef<
  FoodSearchDialogResultsHandle,
  {
    sections: FoodSearchUI.Section[];
    isLoading: boolean;
    onSelectItem: (item: Food.Item) => void;
    className?: string;
  }
>(function FoodSearchDialogResults({ sections, isLoading, onSelectItem, className }, ref) {
  const rows = useMemo(
    () =>
      sections.flatMap((section) =>
        section.data.map((row) => ({
          sectionTitle: section.title,
          sectionExpandButton: section.expandButton,
          row,
        })),
      ),
    [sections],
  );

  if (rows.length === 0) {
    return (
      <div className="pt-[10.5rem] text-center">
        <Text variant="sm" className="text-[#6b7280]">
          {isLoading ? "Loading..." : "No results found"}
        </Text>
      </div>
    );
  }

  return (
    <FoodSearchDialogResultsInner
      key={rows.map(({ row }) => row.key).join("\0")}
      ref={ref}
      rows={rows}
      onSelectItem={onSelectItem}
      className={className}
    />
  );
});

const FoodSearchDialogResultsInner = forwardRef<
  FoodSearchDialogResultsHandle,
  {
    rows: FlatRow[];
    onSelectItem: (item: Food.Item) => void;
    className?: string;
  }
>(function FoodSearchDialogResultsInner({ rows, onSelectItem, className }, ref) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePreparationIndex, setActivePreparationIndex] = useState<number | null>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const activeRow = rows[activeIndex]?.row;

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, activePreparationIndex]);

  const handleKeyDown: FoodSearchDialogResultsHandle["handleKeyDown"] = (event) => {
    if (rows.length === 0) return;
    if (event.target instanceof Element && event.target.closest("[data-category-filter-popover]"))
      return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      if (activePreparationIndex !== null && activeRow?.preparations.length) {
        setActivePreparationIndex((index) =>
          index === null ? 0 : (index + 1) % activeRow.preparations.length,
        );
        return;
      }
      setActivePreparationIndex(null);
      setActiveIndex((index) => (index + 1) % rows.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      if (activePreparationIndex !== null && activeRow?.preparations.length) {
        setActivePreparationIndex((index) =>
          index === null
            ? activeRow.preparations.length - 1
            : (index - 1 + activeRow.preparations.length) % activeRow.preparations.length,
        );
        return;
      }
      setActivePreparationIndex(null);
      setActiveIndex((index) => (index - 1 + rows.length) % rows.length);
      return;
    }

    if (event.key === "ArrowRight") {
      if (!activeRow?.preparations.length) return;
      event.preventDefault();
      event.stopPropagation();
      setActivePreparationIndex((index) => {
        if (index === null) return 0;
        return Math.min(index + 1, activeRow.preparations.length - 1);
      });
      return;
    }

    if (event.key === "ArrowLeft") {
      if (activePreparationIndex === null) return;
      event.preventDefault();
      event.stopPropagation();
      setActivePreparationIndex(null);
      return;
    }

    if (event.key === "Enter") {
      const preparation =
        activePreparationIndex === null
          ? undefined
          : activeRow?.preparations[activePreparationIndex];
      const item = preparation?.item ?? activeRow?.item;
      if (!item) return;
      event.preventDefault();
      event.stopPropagation();
      onSelectItem(item);
    }
  };

  useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

  return (
    <div className={cn("space-y-3", className)} aria-label="Food search results">
      {groupRows(rows).map((section) => (
        <section key={section.title}>
          <SectionHeader title={section.title} expandButton={section.expandButton} />
          <div className="divide-y divide-[#f0f2f5] bg-white">
            {section.rows.map(({ row, globalIndex }) => (
              <ResultRow
                key={row.key}
                row={row}
                index={globalIndex}
                activeIndex={activeIndex}
                activePreparationIndex={activePreparationIndex}
                rowRefs={rowRefs}
                setActiveIndex={setActiveIndex}
                setActivePreparationIndex={setActivePreparationIndex}
                onSelectItem={onSelectItem}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
});

function ResultRow({
  row,
  index,
  activeIndex,
  activePreparationIndex,
  rowRefs,
  setActiveIndex,
  setActivePreparationIndex,
  onSelectItem,
}: {
  row: FoodSearchUI.Row;
  index: number;
  activeIndex: number;
  activePreparationIndex: number | null;
  rowRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  setActiveIndex: (index: number) => void;
  setActivePreparationIndex: (index: number | null) => void;
  onSelectItem: (item: Food.Item) => void;
}) {
  const isActive = activeIndex === index;
  const showPreparationMenu =
    isActive && activePreparationIndex !== null && row.preparations.length > 0;

  return (
    <div
      ref={(element) => {
        rowRefs.current[index] = element;
      }}
      role="button"
      tabIndex={-1}
      className={cn(
        "flex w-full items-start justify-between gap-2 px-2 py-1.5 text-left outline-none transition-colors has-[button:hover]:bg-transparent has-[button[data-preparation-open=true]]:bg-transparent",
        isActive && "bg-accent text-accent-foreground",
        !isActive && "hover:bg-[#f7f8fa]",
      )}
      onMouseEnter={() => {
        setActiveIndex(index);
        setActivePreparationIndex(null);
      }}
      onClick={() => onSelectItem(row.item)}
    >
      <div className="min-w-0 flex-1">
        <FoodItemLabel item={row.item} />
      </div>
      {row.preparations.length > 0 ? (
        <PreparationPopover
          row={row}
          open={showPreparationMenu}
          activePreparationIndex={activePreparationIndex}
          setActivePreparationIndex={setActivePreparationIndex}
          onSelectItem={onSelectItem}
        />
      ) : null}
    </div>
  );
}

function PreparationPopover({
  row,
  open,
  activePreparationIndex,
  setActivePreparationIndex,
  onSelectItem,
}: {
  row: FoodSearchUI.Row;
  open: boolean;
  activePreparationIndex: number | null;
  setActivePreparationIndex: (index: number | null) => void;
  onSelectItem: (item: Food.Item) => void;
}) {
  return (
    <Popover open={open} modal={false}>
      <PopoverTrigger
        render={
          <button
            type="button"
            data-preparation-open={open ? "true" : undefined}
            className="shrink-0"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setActivePreparationIndex(open ? null : 0);
            }}
          />
        }
      >
        <Badge variant="outline" className="bg-transparent hover:bg-gray-100">
          {row.preparationCountLabel ?? "Preparations"}
          <ChevronRightIcon className="size-3" />
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        initialFocus={false}
        className="w-max min-w-max max-w-[calc(100vw-1rem)] gap-0 p-1"
      >
        {row.preparations.map((preparation, index) => (
          <button
            key={preparation.key}
            type="button"
            className={cn(
              "flex min-h-7 w-full cursor-default items-center rounded-md px-2 py-1 text-left text-xs/relaxed outline-hidden hover:bg-accent hover:text-accent-foreground",
              activePreparationIndex === index && "bg-accent text-accent-foreground",
            )}
            onMouseEnter={() => setActivePreparationIndex(index)}
            onClick={(event) => {
              event.stopPropagation();
              onSelectItem(preparation.item);
            }}
          >
            <FoodItemLabel item={preparation.item} mode="preparation" />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function SectionHeader({
  title,
  expandButton,
}: {
  title: ReactNode;
  expandButton: FoodSearchUI.Section["expandButton"];
}) {
  return (
    <div className="flex flex-row items-center justify-between px-1 py-1">
      <Text variant="sm" className="font-semibold text-[#111827]">
        {title}
      </Text>
      {expandButton ? (
        <button
          type="button"
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]"
          onClick={expandButton.onToggle}
        >
          {expandButton.label}
        </button>
      ) : null}
    </div>
  );
}

function groupRows(rows: FlatRow[]) {
  const sections: Array<{
    title: string;
    expandButton: FoodSearchUI.Section["expandButton"];
    rows: Array<FlatRow & { globalIndex: number }>;
  }> = [];
  rows.forEach((row, globalIndex) => {
    let section = sections.find((item) => item.title === row.sectionTitle);
    if (!section) {
      section = { title: row.sectionTitle, expandButton: row.sectionExpandButton, rows: [] };
      sections.push(section);
    }
    section.rows.push({ ...row, globalIndex });
  });
  return sections;
}
